// Pure helpers for the AnnotationCanvas (PRD §9.5, B2 PR5 / B2e).
//
// Lives in its own file so AnnotationCanvas.tsx contains only the
// component export — keeps Vite's react-refresh happy and lets the
// coord/serialization math be unit-tested without rendering the
// component.
//
// Convention: every coordinate persisted to the server is **normalized
// 0..1** against the canvas's bounding rect. That way the same
// annotation renders consistently regardless of viewer's display size
// and survives playback at a different proxy resolution. The component
// converts pointer pixels → normalized at draw time, and normalized →
// SVG units at render time.

import type { components } from '../api/schema';

export type StrokePrimitive = components['schemas']['StrokePrimitive'];
export type ToolType = StrokePrimitive['type']; // 'rect' | 'ellipse' | 'arrow' | 'stroke' | 'text'

/** Clamp a value to [0, 1]. Values outside this range are pointer
 *  events that strayed off the canvas; the cleanest UX is to pin them
 *  to the edge rather than store coords that would render off-screen. */
export function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

/** Convert a pixel-space coordinate to normalized 0..1, given the
 *  containing dimension's pixel size. Returns 0 when `dim` is 0 or
 *  non-finite (canvas hasn't laid out yet) — the caller can guard on
 *  the bounding rect, but this defends against div-by-zero either way. */
export function normalize(px: number, dim: number): number {
  if (!Number.isFinite(dim) || dim <= 0) return 0;
  return clamp01(px / dim);
}

/** Inverse of `normalize`. Multiplies a 0..1 value by the dimension to
 *  recover a pixel-space coordinate. */
export function denormalize(n: number, dim: number): number {
  return clamp01(n) * (Number.isFinite(dim) && dim > 0 ? dim : 0);
}

/** Build the bounding box `{ x, y, w, h }` from two corner points
 *  (start + end of a drag), normalized 0..1. Always returns positive
 *  width/height — the user can drag in any direction. */
export function bboxFromDrag(
  a: [number, number],
  b: [number, number]
): { x: number; y: number; w: number; h: number } {
  const x = Math.min(a[0], b[0]);
  const y = Math.min(a[1], b[1]);
  const w = Math.abs(b[0] - a[0]);
  const h = Math.abs(b[1] - a[1]);
  return { x, y, w, h };
}

interface StrokeStyle {
  color: string;
  width: number;
}

/**
 * Serialize a draft (tool + raw points, all normalized 0..1) to the
 * StrokePrimitive shape the backend expects.
 *
 * - rect / ellipse: bounding box from first → last point
 * - arrow: { from: first, to: last }
 * - stroke (freehand): { points: [...] }
 * - text: { x, y, text, font_size } at the click point
 *
 * Caller is responsible for ensuring `points` has at least one
 * coordinate; an empty draft is meaningless and should be discarded
 * before this is invoked.
 */
export function buildStroke(
  tool: ToolType,
  points: [number, number][],
  style: StrokeStyle,
  text?: string,
  fontSize?: number
): StrokePrimitive {
  if (points.length === 0) {
    throw new Error('buildStroke: points must not be empty');
  }
  const first = points[0] ?? [0, 0];
  const last = points[points.length - 1] ?? first;
  const base = { color: style.color, width: style.width };
  switch (tool) {
    case 'rect': {
      const box = bboxFromDrag(first, last);
      return { type: 'rect', ...base, x: box.x, y: box.y, w: box.w, h: box.h };
    }
    case 'ellipse': {
      const box = bboxFromDrag(first, last);
      return {
        type: 'ellipse',
        ...base,
        x: box.x,
        y: box.y,
        w: box.w,
        h: box.h,
      };
    }
    case 'arrow': {
      return { type: 'arrow', ...base, from: [first[0], first[1]], to: [last[0], last[1]] };
    }
    case 'stroke': {
      return { type: 'stroke', ...base, points: points.map((p) => [p[0], p[1]]) };
    }
    case 'text': {
      return {
        type: 'text',
        ...base,
        x: first[0],
        y: first[1],
        text: text ?? '',
        font_size: fontSize ?? 16,
      };
    }
  }
}
